import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Input } from '../../../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Plus, X, MapPin } from 'lucide-react';

/**
 * Shared searchable + addable selectbox for Location fields (ID-based).
 *
 * Props:
 *  - value: string (locationId)
 *  - displayValue: string (location name, for display when locations haven't loaded yet)
 *  - onChange: (locationId: string, locationName: string) => void
 *  - projectId: string
 *  - error: boolean | string
 *  - placeholder: string
 */
export const LocationSelect = ({ value, displayValue, onChange, projectId, error, placeholder = 'Select location...', open: controlledOpen, onOpenChange }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (val) => { if (onOpenChange) onOpenChange(val); if (!isControlled) setInternalOpen(val); };
  const [search, setSearch] = useState('');
  const [locations, setLocations] = useState([]);

  const fetchLocations = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API}/electrical-systems/${projectId}/locations`);
      setLocations(res.data);
    } catch { /* silent */ }
  }, [projectId]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const selectedName = (locations.find(l => l.id === value) || {}).name || displayValue || '';
  const filtered = locations.filter(l => l.name.toLowerCase().includes(search.toLowerCase()));
  const showAddNew = search.trim() && !locations.some(l => l.name.toLowerCase() === search.trim().toLowerCase());

  const handleSelect = (loc) => {
    onChange(loc.id, loc.name);
    setSearch('');
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('', '');
  };

  const handleAddNew = async () => {
    const name = search.trim();
    try {
      const res = await axios.post(`${API}/electrical-systems/${projectId}/locations`, { name });
      const newLoc = { id: res.data.id, name: res.data.name || name };
      setLocations(prev => [...prev, newLoc].sort((a, b) => a.name.localeCompare(b.name)));
      handleSelect(newLoc);
    } catch {
      handleSelect({ id: '', name });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className={`flex items-center border rounded-md px-3 py-2 cursor-pointer ${error ? 'border-red-500' : 'border-input'}`}
          data-testid="location-select"
        >
          {selectedName ? (
            <div className="flex items-center justify-between w-full">
              <span className="text-sm flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-slate-400" />{selectedName}</span>
              <X className="h-4 w-4 text-slate-400 hover:text-slate-600 shrink-0" onClick={handleClear} />
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">{placeholder}</span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] max-h-48 overflow-y-auto" align="start" sideOffset={4}>
        <div className="sticky top-0 bg-white p-2 border-b">
          <Input
            className="h-8 text-sm"
            placeholder="Search or type new location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            data-testid="location-search-input"
          />
        </div>
        {filtered.map(loc => (
          <div
            key={loc.id}
            className={`px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm flex items-center gap-1.5 ${loc.id === value ? 'bg-slate-50 font-medium' : ''}`}
            onClick={() => handleSelect(loc)}
            data-testid={`location-opt-${loc.name}`}
          >
            <MapPin className="h-3.5 w-3.5 text-slate-300 shrink-0" />
            {loc.name}
          </div>
        ))}
        {showAddNew && (
          <div
            className="px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm text-orange-600 font-medium border-t flex items-center gap-1"
            onClick={handleAddNew}
            data-testid="location-add-new"
          >
            <Plus className="h-3.5 w-3.5" />Add: &quot;{search.trim()}&quot;
          </div>
        )}
        {filtered.length === 0 && !showAddNew && (
          <div className="px-3 py-2 text-sm text-slate-400">No locations found</div>
        )}
      </PopoverContent>
    </Popover>
  );
};
